<template>
    <v-img
        class="kk-card"
        :class="{
            'extra-messages-1': extraPinMessages.length === 1,
            'extra-messages-2': extraPinMessages.length === 2,
        }"
        :src="
            $vuetify.breakpoint.smAndDown
                ? require('@/assets/card/enter-pin-xs.png')
                : require('@/assets/card/enter-pin-md.png')
        "
        position="center top"
    >
        <div class="kk-card-title justify-center d-none d-md-block" />
        <v-card
            class="mx-auto card-info d-none d-md-block"
            width="308"
            height="auto"
            outlined
        >
            <pin-info :extraPinMessages="extraPinMessages"></pin-info>
            <v-divider class="divider"></v-divider>
            <v-text-field
                class="card-input"
                :class="{ 'dim-text': pin.length === 20 }"
                autocomplete="false"
                v-model="pin"
                label="ENTER PIN"
                height="52"
                :maxlength="20"
                solo
                autocorrect="off"
                autocapitalize="off"
                spellcheck="false"
                :error-messages="error"
                @keyup.enter="trySubmitting"
            ></v-text-field>
            <vue-recaptcha
                v-if="recaptcha.loaded"
                ref="recaptcha"
                :sitekey="recaptchaSiteKey"
                size="invisible"
                @verify="recaptchaV2Verified"
                @expired="resetRecaptcha"
            />
        </v-card>
        <div>
            <div
                class="d-block d-md-none extra-messages"
                v-for="(message, i) in extraPinMessages"
                :key="i"
            >
                <span class="extra-message">* {{ message }}</span>
            </div>
        </div>
        <v-text-field
            class="card-input d-md-none"
            :class="{ 'dim-text': pin.length === 20 }"
            autocomplete="false"
            v-model="pin"
            label="ENTER PIN"
            height="52"
            :maxlength="20"
            solo
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            :error-messages="error"
            @keyup.enter="trySubmitting"
        ></v-text-field>
        <div class="btn-wrapper">
            <recaptcha-attribution class="recaptcha-attribution" />
            <v-btn
                class="card-btn"
                rounded
                :disabled="disableSubmit"
                :loading="loading"
                @click="fetchBalanceHandler()"
                >CHECK BALANCE</v-btn
            >
        </div>
    </v-img>
</template>

<script>
import KarmakoinAPI from '@/modules/karmakoin-api';
import pinInfo from '@/components/card/state/enter-pin/pin-info';
import VueRecaptcha from 'vue-recaptcha';
import recaptchaKeys from '@/config/recaptcha-key';
import recaptchaAttribution from '@/components/recaptcha/recaptcha-attribution';
import recaptchaV3Wrapper from '@/plugins/recaptcha-v3-wrapper';
import { CHECK_BALANCE, COMBINE_CARDS } from '@/constatns';

export default {
    name: 'enter-pin',
    components: { pinInfo, VueRecaptcha, recaptchaAttribution },
    props: {
        extraPinMessages: {
            type: Array,
            default: () => [],
        },
        disablePinSubmit: {
            type: Boolean,
            default: false,
        },
        sendingPin: {
            type: String,
            default: undefined,
        },
        sendingCurrency: {
            type: String,
            default: undefined,
        },
    },
    data() {
        return {
            pin: '',
            balance: '',
            currency: '',
            error: '',
            loading: false,
            reCaptchaCode: '',
            showCaptcha: false,
            recaptcha: {
                loaded: false,
                action: {
                    web: 'check_balance',
                },
                token: '',
                version: '',
            },
        };
    },
    computed: {
        pinMatch() {
            return (
                this.sendingPin &&
                this.sendingPin.toLowerCase() === this.pin.toLowerCase()
            );
        },
        currencyMatch() {
            return this.sendingCurrency === this.currency;
        },
        disableSubmit() {
            return (
                !this.pin ||
                this.pin.includes(' ') ||
                this.pin.toLowerCase().includes('i') ||
                this.pin.toLowerCase().includes('o') ||
                this.disablePinSubmit ||
                this.pinMatch
            );
        },
        recaptchaSiteKey() {
            return recaptchaKeys.v2;
        },
    },
    mounted() {
        recaptchaV3Wrapper.isRecaptchaLoaded(this).then(() => {
            this.recaptcha.loaded = true;
        });
    },
    methods: {
        sendAnalyticsEvent(result) {
            let action = {};
            let event;
            if (this.extraPinMessages.length === 1) {
                action = { action: 'Card 1 - Sending Funds' };
                event = { event: COMBINE_CARDS };
            } else if (this.extraPinMessages.length === 2) {
                action = { action: 'Card 2 - Receiving Funds' };
                event = { event: COMBINE_CARDS };
            } else {
                event = { event: CHECK_BALANCE };
            }
            window.dataLayer.push({
                ...action,
                ...event,
                ...result,
            });
        },
        trySubmitting() {
            if (this.disableSubmit) {
                this.error = 'Invalid PIN';
            } else {
                this.fetchBalanceHandler();
            }
        },
        async requestFetchBalance() {
            await KarmakoinAPI.fetchBalance(
                this.pin,
                this.recaptcha.token,
                this.recaptcha.version
            )
                .then(({ amount, currency }) => {
                    this.loading = false;
                    this.currency = currency;
                    if (this.extraPinMessages.length === 0) {
                        this.sendAnalyticsEvent({
                            'balance result': 'valid',
                        });
                    } else {
                        this.sendAnalyticsEvent({
                            'combine cards result': 'valid',
                        });
                    }
                    if (this.sendingCurrency && !this.currencyMatch) {
                        this.error = 'Currencies must match.';
                        this.loading = false;
                        return;
                    }
                    this.$emit('balance-fetched', {
                        amount,
                        currency,
                        pin: this.pin,
                    });
                })
                .catch((err) => {
                    const code = err.data.Response.ResponseCode;
                    if (this.extraPinMessages.length === 0) {
                        this.sendAnalyticsEvent({
                            'balance result': 'invalid',
                        });
                    } else if (code === 203) {
                        this.sendAnalyticsEvent({
                            'combine cards result': 'error - balance zero',
                        });
                    } else if (code === 404 || code === 406) {
                        this.sendAnalyticsEvent({
                            'combine cards result': 'error - invalid code',
                        });
                    } else {
                        this.sendAnalyticsEvent({
                            'combine cards result': 'error - unclassified',
                        });
                    }
                    if (code === 203) {
                        this.error =
                            'This card has a zero balance and is no longer redeemable.';
                    } else if (code === 400) {
                        this.error =
                            'Your request cannot be fulfilled at this time due to a configuration issue. Contact Nexon Game Card support for assistance.';
                    } else if (code === 401) {
                        this.error =
                            'Merchant configuration error. Contact Nexon Game Card support to whitelist your server IP.';
                    } else if (code === 404) {
                        this.error =
                            'The PIN you entered is invalid. Please try again.';
                    } else if (code === 406) {
                        this.error =
                            'This card is not redeemable. Contact the merchant from whom you purchased the card for assistance.';
                    } else {
                        this.error =
                            'Oops! Something went wrong. Please refresh the page and try again.';
                    }
                    this.loading = false;
                })
                .catch(() => {
                    this.error = 'An unkown error occurred.';
                    this.sendAnalyticsEvent({
                        'balance result': 'error - unclassified',
                    });
                });
        },
        fetchBalanceHandler() {
            if (this.pin) {
                this.error = '';
                this.loading = true;
                if (this.recaptcha.version === 'v2') {
                    this.requestFetchBalance();
                } else {
                    recaptchaV3Wrapper
                        .getRecaptchaV3Token(this, this.recaptcha.action.web)
                        .then((token) => {
                            this.setRecaptchaTokenValues(token, 'v3');
                        })
                        .then(() => {
                            this.requestFetchBalance();
                        })
                        .catch(() => {
                            this.error = 'Recaptcha error occurred.';
                        });
                }
            }
        },
        recaptchaV2Verified(token) {
            this.setRecaptchaTokenValues(token, 'v2');
            this.fetchBalanceHandler();
        },
        setRecaptchaTokenValues(token, version) {
            this.recaptcha.token = token;
            this.recaptcha.version = version;
        },
        resetRecaptcha() {
            if (this.$refs.recaptcha) {
                this.$refs.recaptcha.reset();
                this.setRecaptchaTokenValues('', '');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.kk-card {
    position: relative;
    height: 300px;
    width: 300px;
    @include break('sm') {
        width: 500px;
    }
    @include break('md') {
        width: 380px;
        height: 562px;
    }
    background-color: black;
    @include break('md') {
        background: none;
    }
    .kk-card-title {
        height: 26px;
        padding: 0;
        margin: 96px auto 0 auto;
    }
    .card-info {
        border-radius: 6px;
        border: solid 1px rgba(151, 151, 151, 0.39);
        padding: 20px 20px 0 20px;
        margin: 67px 36px 0 36px;

        .divider {
            border-color: rgba(151, 151, 151, 0.39);
        }
    }
    .card-input {
        margin: 79px 20px 0 20px;
        @include break('sm') {
            margin: 79px 26px 0 26px;
        }
        @include break('md') {
            margin: 18px 0 8px 0;
        }

        border-radius: 9px;
    }
    .btn-wrapper {
        width: 100%;
        margin: 0 auto;
        .recaptcha-attribution {
            margin: 0 15px 15px 15px;
            @include break('md') {
                margin: 15px 36px;
            }
        }
        .card-btn {
            width: 260px;
            height: 54px;
            background-color: #2960e8 !important;
            color: #ffffff;
            @include break('sm') {
                width: 447px;
            }
            @include break('md') {
                width: 308px;
            }
            position: relative;
        }
    }
    &.extra-messages-1 {
        .extra-messages {
            margin-top: 62px;
            @include break('sm') {
                margin-top: 51px;
            }
        }
        .card-input {
            margin-top: 10px;
            @include break('md') {
                margin-top: 18px;
                margin-bottom: 8px;
            }
        }
        .card-info {
            margin: 57px auto 0 auto;
        }
    }
    &.extra-messages-2 {
        .extra-messages {
            &:first-of-type {
                margin-top: 50px;
            }
        }

        .card-input {
            margin-top: 10px;
            @include break('md') {
                margin-top: 18px;
                margin-bottom: 8px;
            }
        }
        .card-info {
            margin: 42px auto 0 auto;
        }
        .recaptcha-attribution {
            margin: 0 15px;
            @include break('md') {
                margin: 10px 36px;
            }
        }
    }
    .extra-messages {
        text-align: left;
        padding-left: 20px;
        padding-right: 20px;
        line-height: 14px;
        @include break('sm') {
            padding-left: 26px;
            padding-right: 26px;
        }

        .extra-message {
            font-size: 11px;
            color: gray;
            line-height: 1.45;
        }
    }
}
</style>

<style lang="scss">
.card-input {
    .v-text-field__details {
        height: auto;
        overflow: visible;
        min-height: 0;
        margin: 4px 0 -4px 0;
        .error--text {
            font-size: 11px !important;
            color: #e92852 !important;
        }
    }
    &.v-input--is-focused {
        .v-input__slot {
            border: solid 1px #2960e8 !important;
        }
        .v-label {
            display: none;
        }
    }
    &.dim-text {
        .v-input__slot {
            input {
                color: #7b7b7b;
            }
        }
        &.v-input--is-focused {
            .v-input__slot {
                border: solid 1px rgba(0, 0, 0, 0.3) !important;
            }
        }
    }
    .v-input__slot {
        margin-bottom: 0;
        box-shadow: inset 1px 1px 0 0 #c0c0c0 !important;
        &:hover {
            border: solid 1px #2960e8 !important;
        }
        border: solid 1px rgba(0, 0, 0, 0.3) !important;
        input {
            text-align: center;
            font-weight: 500;
            caret-color: black !important;
        }
    }
    .v-label {
        font-size: 14px;
        font-weight: bold;
        left: 80px !important;
        @include break('sm') {
            left: 176px !important;
        }
        @include break('md') {
            left: 84px !important;
        }
        color: #b3b3b3;
    }
}
.kk-card {
    .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
        color: white !important;
        background-color: #2960e8 !important;
        opacity: 0.3;
        cursor: not-allowed;
    }
    .v-btn:not(.v-btn--text):not(.v-btn--outlined):hover {
        background-color: #2960e8 !important;
        box-shadow: 0 8px 15px 0 #5776cf;
    }
    .v-btn:not(.v-btn--text):not(.v-btn--outlined):hover:before {
        opacity: 0 !important;
    }
    .v-btn:not(.v-btn--text):not(.v-btn--outlined):focus:before {
        opacity: 0 !important;
    }
    .v-btn:not(.v-btn--text):not(.v-btn--outlined):focus {
        background-color: #2960e8 !important;
        box-shadow: none;
    }
    .v-btn__loader {
        top: inherit;
    }
    .v-ripple__container {
        display: none;
    }
}
</style>
